import { useState } from 'react'

import AssetDetailContent, { type AssetDetailTab } from '@/components/ui/asset-detail/AssetDetailContent'
import AssetDetailHeader from '@/components/ui/asset-detail/AssetDetailHeader'
import { DetailSheet } from '@/components/ui/detail-sheet/DetailSheet'
import { useAssetState } from '@/hooks'

type AssetDetailSheetProps = {
  isOpen: boolean
  handleClose: () => void
  assetId: number | undefined
  companyId: number | undefined
  fourthColRightEdge?: number | null
  /** The asset table's header row and right edge, relative to the sheet's positioned container. */
  tableHeader?: { top: number; height: number; left: number }
  variant?: 'overlay' | 'pane'
}

/**
 * The asset detail view on the asset map. As an overlay it starts at the asset column's right
 * edge, after its alert, power and gateway cells (the table hides its other columns while the
 * sheet is open).
 */
export function AssetDetailSheet({
  isOpen,
  assetId,
  companyId,
  handleClose,
  fourthColRightEdge,
  tableHeader,
  variant = 'overlay',
}: AssetDetailSheetProps) {
  const { data: asset } = useAssetState(assetId)
  // Held here rather than in the content, so the tab survives switching assets.
  const [activeTab, setActiveTab] = useState<AssetDetailTab>('overview')

  return (
    <DetailSheet
      isOpen={isOpen}
      variant={variant}
      tableHeader={tableHeader}
      fallbackLeft={fourthColRightEdge ?? undefined}
      bodyKey={asset?.id}
      header={
        asset && (
          <AssetDetailHeader
            asset={asset}
            onClose={handleClose}
            tooltipPosition="left"
            compact={variant === 'overlay'}
          />
        )
      }
    >
      {asset && (
        <AssetDetailContent asset={asset} companyId={companyId} activeTab={activeTab} setActiveTab={setActiveTab} />
      )}
    </DetailSheet>
  )
}
